import { useEffect, useRef, useState } from 'react';
import { uploadSessionFilesWithProgress } from '../constants';

export const LEGEND_DOC_ACCEPT =
  '.pdf,.doc,.docx,.txt,.md,.rtf,.csv,.xlsx,.xls,.png,.jpg,.jpeg,.webp,application/pdf,text/plain';

const LEGEND_OPS_DRAFT_PREFIX = 'legend_ops_draft:';

export function emptyLegendOpsDraft() {
  return {
    caseName: '',
    osintNotes: '',
    socialLinks: '',
    direction: '',
    exerciseGoal: '',
    vectors: '',
    targetingLimits: '',
    successCriteria: '',
    priorityTargets: '',
  };
}

export function normalizeLegendOpsDraft(raw) {
  if (!raw || typeof raw !== 'object') {
    return emptyLegendOpsDraft();
  }
  return {
    caseName: String(raw.caseName || raw.case_name || ''),
    osintNotes: String(raw.osintNotes || raw.osint_notes || ''),
    socialLinks: String(raw.socialLinks || raw.social_links || ''),
    direction: String(raw.direction || ''),
    exerciseGoal: String(raw.exerciseGoal || raw.exercise_goal || ''),
    vectors: String(raw.vectors || ''),
    targetingLimits: String(raw.targetingLimits || raw.targeting_limits || ''),
    successCriteria: String(raw.successCriteria || raw.success_criteria || ''),
    priorityTargets: String(raw.priorityTargets || raw.priority_targets || ''),
  };
}

export function toLegendOpsDraftPayload(draft) {
  const normalized = normalizeLegendOpsDraft(draft);
  return {
    case_name: normalized.caseName,
    osint_notes: normalized.osintNotes,
    social_links: normalized.socialLinks,
    direction: normalized.direction,
    exercise_goal: normalized.exerciseGoal,
    vectors: normalized.vectors,
    targeting_limits: normalized.targetingLimits,
    success_criteria: normalized.successCriteria,
    priority_targets: normalized.priorityTargets,
  };
}

export function legendOpsDraftHasContent(draft) {
  const normalized = normalizeLegendOpsDraft(draft);
  return Boolean(
    normalized.caseName.trim()
    || normalized.osintNotes.trim()
    || normalized.socialLinks.trim()
    || normalized.direction.trim()
    || normalized.exerciseGoal.trim()
    || normalized.vectors.trim()
    || normalized.targetingLimits.trim()
    || normalized.successCriteria.trim()
    || normalized.priorityTargets.trim(),
  );
}

export function resolveLegendOpsDraft(conversation) {
  const apiDraft = normalizeLegendOpsDraft(conversation?.legend_ops_draft);
  const storageDraft = loadLegendOpsDraft(conversation?.session_id);
  const merged = {
    ...(legendOpsDraftHasContent(apiDraft) ? apiDraft : storageDraft),
  };
  if (!merged.caseName.trim()) {
    const title = String(conversation?.title || conversation?.report?.title || '').trim();
    if (title) {
      merged.caseName = title;
    }
  }
  return merged;
}

export function legendOpsDraftKey(draft) {
  const normalized = normalizeLegendOpsDraft(draft);
  return JSON.stringify(normalized);
}

/** Compose Ronin intake answers into a binding direction block for the pipeline. */
export function buildRoninDirectionFromIntake(draft) {
  const normalized = normalizeLegendOpsDraft(draft);
  const blocks = [];
  if (normalized.exerciseGoal.trim()) {
    blocks.push(`GOAL OF EXERCISE:\n${normalized.exerciseGoal.trim()}`);
  }
  if (normalized.vectors.trim()) {
    blocks.push(`VECTORS TO USE:\n${normalized.vectors.trim()}`);
  }
  if (normalized.targetingLimits.trim()) {
    blocks.push(`TARGETING LIMITS / ROE CONSTRAINTS:\n${normalized.targetingLimits.trim()}`);
  }
  if (normalized.successCriteria.trim()) {
    blocks.push(`SUCCESS CRITERIA / DESIRED END STATE:\n${normalized.successCriteria.trim()}`);
  }
  if (normalized.priorityTargets.trim()) {
    blocks.push(`PRIORITY TARGETS / ROLES:\n${normalized.priorityTargets.trim()}`);
  }
  if (normalized.direction.trim()) {
    blocks.push(`ADDITIONAL DIRECTION:\n${normalized.direction.trim()}`);
  }
  return blocks.join('\n\n');
}

function applyLegendOpsDraftToSetters(draft, setters) {
  const normalized = normalizeLegendOpsDraft(draft);
  setters.setCaseName(normalized.caseName);
  setters.setOsintNotes(normalized.osintNotes);
  setters.setSocialLinks(normalized.socialLinks);
  setters.setDirection(normalized.direction);
  setters.setExerciseGoal(normalized.exerciseGoal);
  setters.setVectors(normalized.vectors);
  setters.setTargetingLimits(normalized.targetingLimits);
  setters.setSuccessCriteria(normalized.successCriteria);
  setters.setPriorityTargets(normalized.priorityTargets);
  return legendOpsDraftKey(normalized);
}

/** Shared Legend form state with durable draft flush on unmount / session change. */
export function useLegendOpsDraftFields({ savedDraft, sessionId, onDraftChange }) {
  const [caseName, setCaseName] = useState(savedDraft?.caseName || '');
  const [osintNotes, setOsintNotes] = useState(savedDraft?.osintNotes || '');
  const [socialLinks, setSocialLinks] = useState(savedDraft?.socialLinks || '');
  const [direction, setDirection] = useState(savedDraft?.direction || '');
  const [exerciseGoal, setExerciseGoal] = useState(savedDraft?.exerciseGoal || '');
  const [vectors, setVectors] = useState(savedDraft?.vectors || '');
  const [targetingLimits, setTargetingLimits] = useState(savedDraft?.targetingLimits || '');
  const [successCriteria, setSuccessCriteria] = useState(savedDraft?.successCriteria || '');
  const [priorityTargets, setPriorityTargets] = useState(savedDraft?.priorityTargets || '');
  // Last draft key we applied locally or pushed upstream — ignore parent echoes.
  const draftSyncRef = useRef(legendOpsDraftKey(savedDraft));
  const sessionRef = useRef(sessionId);
  const onDraftChangeRef = useRef(onDraftChange);
  const localDraftRef = useRef({
    caseName,
    osintNotes,
    socialLinks,
    direction,
    exerciseGoal,
    vectors,
    targetingLimits,
    successCriteria,
    priorityTargets,
  });

  useEffect(() => {
    onDraftChangeRef.current = onDraftChange;
  }, [onDraftChange]);

  useEffect(() => {
    localDraftRef.current = {
      caseName,
      osintNotes,
      socialLinks,
      direction,
      exerciseGoal,
      vectors,
      targetingLimits,
      successCriteria,
      priorityTargets,
    };
  }, [
    caseName,
    osintNotes,
    socialLinks,
    direction,
    exerciseGoal,
    vectors,
    targetingLimits,
    successCriteria,
    priorityTargets,
  ]);

  // Hydrate from parent only on session change or a true external draft load.
  // Do NOT reset on every parent echo of our own onDraftChange — that fights typing.
  useEffect(() => {
    const incomingKey = legendOpsDraftKey(savedDraft);
    const sessionChanged = sessionRef.current !== sessionId;
    if (!sessionChanged && incomingKey === draftSyncRef.current) {
      return;
    }
    sessionRef.current = sessionId;
    draftSyncRef.current = applyLegendOpsDraftToSetters(savedDraft, {
      setCaseName,
      setOsintNotes,
      setSocialLinks,
      setDirection,
      setExerciseGoal,
      setVectors,
      setTargetingLimits,
      setSuccessCriteria,
      setPriorityTargets,
    });
  }, [savedDraft, sessionId]);

  // Debounced persist. Flush only on unmount / session teardown, not on every keystroke cleanup.
  useEffect(() => {
    const next = {
      caseName,
      osintNotes,
      socialLinks,
      direction,
      exerciseGoal,
      vectors,
      targetingLimits,
      successCriteria,
      priorityTargets,
    };
    const key = legendOpsDraftKey(next);
    if (key === draftSyncRef.current) {
      return undefined;
    }
    const timer = setTimeout(() => {
      draftSyncRef.current = key;
      onDraftChangeRef.current?.(next);
    }, 400);
    return () => clearTimeout(timer);
  }, [
    caseName,
    osintNotes,
    socialLinks,
    direction,
    exerciseGoal,
    vectors,
    targetingLimits,
    successCriteria,
    priorityTargets,
  ]);

  // Flush latest local draft when leaving the session/component.
  useEffect(() => () => {
    const next = localDraftRef.current;
    const key = legendOpsDraftKey(next);
    if (key !== draftSyncRef.current) {
      draftSyncRef.current = key;
      onDraftChangeRef.current?.(next);
    }
  }, [sessionId]);

  return {
    caseName,
    setCaseName,
    osintNotes,
    setOsintNotes,
    socialLinks,
    setSocialLinks,
    direction,
    setDirection,
    exerciseGoal,
    setExerciseGoal,
    vectors,
    setVectors,
    targetingLimits,
    setTargetingLimits,
    successCriteria,
    setSuccessCriteria,
    priorityTargets,
    setPriorityTargets,
  };
}

export function loadLegendOpsDraft(sessionId) {
  if (!sessionId) return emptyLegendOpsDraft();
  try {
    const raw = sessionStorage.getItem(`${LEGEND_OPS_DRAFT_PREFIX}${sessionId}`);
    if (!raw) return emptyLegendOpsDraft();
    return normalizeLegendOpsDraft(JSON.parse(raw));
  } catch {
    return emptyLegendOpsDraft();
  }
}

export function persistLegendOpsDraft(sessionId, draft) {
  if (!sessionId) return;
  try {
    sessionStorage.setItem(
      `${LEGEND_OPS_DRAFT_PREFIX}${sessionId}`,
      JSON.stringify(normalizeLegendOpsDraft(draft)),
    );
  } catch {
    // Ignore quota / private-mode failures; in-memory App state still isolates chats.
  }
}

export function clearLegendOpsDraft(sessionId) {
  if (!sessionId) return;
  try {
    sessionStorage.removeItem(`${LEGEND_OPS_DRAFT_PREFIX}${sessionId}`);
  } catch {
    // no-op
  }
}

export async function uploadLegendCaseFiles({
  sessionId,
  fileList,
  existingFiles = [],
  onProgress,
  onStatus,
  uploadKind = 'knowledge',
}) {
  const files = Array.from(fileList || []);
  if (!files.length) {
    return existingFiles;
  }

  // One file per request: avoids Cloudflare/nginx multi-file body caps and
  // keeps progress accurate for large Counsel/Parley case packs.
  const next = [...existingFiles];
  const totalBytes = files.reduce((sum, file) => sum + (file.size || 0), 0) || files.length;
  let completedBytes = 0;

  const kind = uploadKind === 'social_media' ? 'social_media' : 'knowledge';
  const mergeUploaded = (payload, fallbackName) => {
    const uploaded = Array.isArray(payload?.files)
      ? payload.files
      : Array.isArray(payload?.uploaded_files)
        ? payload.uploaded_files
        : [{ filename: fallbackName, name: fallbackName, upload_kind: kind }];
    uploaded.forEach((item) => {
      const name = item.filename || item.name || fallbackName;
      if (!name) return;
      if (!next.some((existing) => (existing.filename || existing.name) === name)) {
        next.push({
          filename: name,
          name,
          upload_kind: item.upload_kind || kind,
          file_type: item.file_type,
        });
      }
    });
  };

  if (kind === 'social_media') {
    onStatus?.(`Uploading social media dump${files.length === 1 ? '' : 's'}…`);
    const kept = next.filter((item) => (item.upload_kind || 'knowledge') !== 'social_media');
    next.length = 0;
    next.push(...kept);
    const payload = await uploadSessionFilesWithProgress({
      sessionId,
      files,
      uploadKind: 'social_media',
      onProgress: (pct) => onProgress?.(Math.min(99, pct)),
    });
    mergeUploaded(payload, files[0].name);
  } else {
    for (let index = 0; index < files.length; index += 1) {
      const file = files[index];
      onStatus?.(`Uploading ${index + 1}/${files.length}: ${file.name}`);
      const payload = await uploadSessionFilesWithProgress({
        sessionId,
        files: [file],
        uploadKind: 'knowledge',
        deferExtraction: true,
        onProgress: (filePct) => {
          if (!onProgress) return;
          const fileSize = file.size || Math.max(totalBytes / files.length, 1);
          const overall = ((completedBytes + (fileSize * filePct) / 100) / totalBytes) * 100;
          onProgress(Math.min(99, Math.round(overall)));
        },
      });
      completedBytes += file.size || Math.max(totalBytes / files.length, 1);
      mergeUploaded(payload, file.name);
    }
  }

  onProgress?.(100);
  onStatus?.('');
  return next;
}

export async function deleteLegendCaseFile({ sessionId, filename, apiFetch }) {
  const name = String(filename || '').trim();
  if (!sessionId || !name) {
    throw new Error('Session and filename are required to delete a document.');
  }
  if (!apiFetch) {
    throw new Error('apiFetch is required to delete a document.');
  }
  const params = new URLSearchParams({ session_id: sessionId, filename: name });
  const response = await apiFetch(`/api/upload_file?${params.toString()}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Failed to delete ${name}`);
  }
  const payload = await response.json().catch(() => ({}));
  if (Array.isArray(payload?.files)) {
    return payload.files.map((item) => ({
      filename: item.filename || item.name,
      name: item.filename || item.name,
      upload_kind: item.upload_kind || 'knowledge',
      file_type: item.file_type,
    }));
  }
  return null;
}
